import { Route } from "react-router-dom"
import { AuthenticatedLayout } from "./wrappers"
import { lazy } from "react"
import { lazyRetry } from "./utils"
import { SHOW_DEV_ONLY_COMPONENTS } from "@/const"
import MultiFactorSetupModal from "@components/Settings/MultiFactorSetupModal.tsx"
import MultiFactorDisableModal from "@components/Settings/MultiFactorDisableModal.tsx"

const PeriodPlannerPage = lazy(() => lazyRetry(() => import("../views/PeriodPlanner/PeriodPlannerPage"), "periodPlanner"))
const Register = lazy(() => lazyRetry(() => import("../views/User/Register"), "register"))
const Home = lazy(() => lazyRetry(() => import("../views/Home"), "home"))
const Settings = lazy(() => lazyRetry(() => import("../views/Settings/Settings"), "settings"))
const TestResult = lazy(() => lazyRetry(() => import("../views/Test/TestResult"), "testResult"))
const TestResultVisualization = lazy(() => lazyRetry(() => import("../views/Test/TestResultVisualization"), "testResultVisualization"))
const PlayerMatchPerformanceRating = lazy(() =>
	lazyRetry(() => import("../views/Diary/MatchRatingCoach"), "playerMatchPerformanceRating")
)
const PeerSurvey = lazy(() => lazyRetry(() => import("../views/Diary/PeerSurvey"), "peerSurvey"))
const InvitePatron = lazy(() => lazyRetry(() => import("../views/RootGroup/InvitePatron"), "invitePatron"))

const Event = lazy(() => lazyRetry(() => import("../views/Event/Event"), "event"))
const InviteMember = lazy(() => lazyRetry(() => import("../views/RootGroup/InviteMemberEmail"), "inviteMember"))
const ConfirmMemberInviteExisting = lazy(() =>
	lazyRetry(() => import("../views/User/ConfirmMemberInviteExisting"), "confirmMemberInviteExisting")
)
const RegistrationMember = lazy(() => lazyRetry(() => import("../views/User/RegistrationMember"), "registrationMember"))
const GroupList = lazy(() => lazyRetry(() => import("../views/Group/GroupList"), "groupList"))
const GroupCreate = lazy(() => lazyRetry(() => import("../views/Group/GroupCreate"), "groupCreate"))
const GroupDetail = lazy(() => lazyRetry(() => import("../views/Group/GroupDetail"), "groupDetail"))
const RootGroupDetail = lazy(() => lazyRetry(() => import("../views/RootGroup/RootGroupDetail"), "rootGroupDetail"))
const RootGroupCreate = lazy(() => lazyRetry(() => import("../views/RootGroup/RootGroupCreate"), "rootGroupCreate"))
const Profile = lazy(() => lazyRetry(() => import("../views/User/Profile"), "profile"))
const ChildAccountConfirmation = lazy(() =>
	lazyRetry(() => import("../views/User/ChildAccountConfirmation"), "childAccountConfirmation")
)
const EditMemberships = lazy(() => lazyRetry(() => import("../views/RootGroup/EditMemberships"), "editMemberships"))
const TestChartsPlayer = lazy(() => lazyRetry(() => import("../views/Charts/TestChartsPlayer"), "testChartsPlayer"))
const TestChartsGroup = lazy(() => lazyRetry(() => import("../views/Charts/TestChartsGroup"), "testChartsGroup"))
const MatchRatingChartsGroup = lazy(() => lazyRetry(() => import("../views/Charts/MatchRatingChartsGroup"), "matchRatingChartsGroup"))
const MatchRatingChartsPlayer = lazy(() =>
	lazyRetry(() => import("../views/Charts/MatchRatingChartsPlayer"), "matchRatingChartsPlayer")
)
const ExerciseChart = lazy(() => lazyRetry(() => import("../views/Charts/ExerciseChart"), "exerciseChart"))
const ExerciseChartWeek = lazy(() => lazyRetry(() => import("../views/Charts/ExerciseChartWeek"), "exerciseChartWeek"))
const ExerciseChartDay = lazy(() => lazyRetry(() => import("../views/Charts/ExerciseChartDay"), "exerciseChartDay"))
const TaskOverview = lazy(() => lazyRetry(() => import("../views/Tasks/TaskOverview"), "taskOverview"))
const VideoGeneral = lazy(() => lazyRetry(() => import("../views/Video/VideoGeneral"), "videoGeneral"))
const VideoRootGroup = lazy(() => lazyRetry(() => import("../views/Video/VideoRootGroup"), "VideoRootGroup"))
const PeriodPlannerEditOrCreate = lazy(() =>
	lazyRetry(() => import("../views/PeriodPlanner/PeriodPlannerEditOrCreate"), "periodPlannerEditOrCreate")
)
const LifeManagementChartsPlayer = lazy(() =>
	lazyRetry(() => import("../views/Charts/LifeManagementChartsPlayer"), "lifeManagementChartsPlayer")
)
const LifeManagementChartsGroup = lazy(() =>
	lazyRetry(() => import("../views/Charts/LifeManagementChartsGroup"), "lifeManagementChartsGroup")
)
const SurveyAnswersGroup = lazy(() => lazyRetry(() => import("../views/Charts/SurveyAnswersGroup"), "surveyAnswersGroup"))
const SurveyAnswersPlayer = lazy(() => lazyRetry(() => import("../views/Charts/SurveyAnswersPlayer"), "surveyAnswersPlayer"))
const Absence = lazy(() => lazyRetry(() => import("../views/Event/Absence"), "absence"))
const ExercisePlanner = lazy(() => lazyRetry(() => import("../views/Event/ExercisePlanner"), "exercisePlanner"))
const PlayerCardList = lazy(() => lazyRetry(() => import("../views/PlayerCard/PlayerCardList"), "playerCardList"))
const PlayerCard = lazy(() => lazyRetry(() => import("../views/PlayerCard/PlayerCard"), "playerCard"))
const DevelopmentPhase = lazy(() => lazyRetry(() => import("../views/Charts/DevelopmentPhase"), "playerCard"))

const EventTemplateList = lazy(() => lazyRetry(() => import("../views/EventTemplate/EventTemplateList"), "eventTemplateList"))
const EventTemplate = lazy(() => lazyRetry(() => import("../views/EventTemplate/EventTemplate"), "eventTemplate"))

const MigrationView = lazy(() => lazyRetry(() => import("../views/Migration/MigrationView"), "migrationView"))
const ExerciseDashboard = lazy(() => lazyRetry(() => import("../views/FFAClubSupport/ExerciseDashboard"), "exerciseDashboard"))
const PrivacyPolicyMarkdown = lazy(() => lazyRetry(() => import("../components/Markdown/PrivacyPolicyMarkdown"), "privacyPolicy"))

// Flag for enabling conditional rendering that hides root group select inputs at 'managerial' pages and uses role selector root group context instead
const USE_ROOT_CTX = true

const MainRoute = (
	<Route>
		<Route path="/" element={<AuthenticatedLayout fullWidth />}>
			<Route index element={<Home />} />
			<Route path="/settings" element={<Settings />}>
				<Route path="enable-mfa" element={<MultiFactorSetupModal />} />
				<Route path="disable-mfa" element={<MultiFactorDisableModal />} />
			</Route>

			<Route path="/charts/personal" element={<TestChartsPlayer />} />
			<Route path="/charts/exercise" element={<ExerciseChart />} />
			<Route path="/charts/group" element={<TestChartsGroup />} />
			<Route path="/charts/lifemanagementGroup" element={<LifeManagementChartsGroup />} />
			<Route path="/charts/surveyAnswersGroup" element={<SurveyAnswersGroup />} />
			<Route path="/charts/surveyAnswersPlayer" element={<SurveyAnswersPlayer />} />
			<Route path="/charts/lifemanagementPersonal" element={<LifeManagementChartsPlayer />} />
			<Route path="/charts/matchratingGroup" element={<MatchRatingChartsGroup />} />
			<Route path="/charts/matchratingPlayer" element={<MatchRatingChartsPlayer />} />
			<Route path="/charts/exercise_week" element={<ExerciseChartWeek />} />
			<Route path="/charts/exercise_day" element={<ExerciseChartDay />} />
			<Route path="/charts/development_phase" element={<DevelopmentPhase />} />
			<Route path="/test_results" element={<TestResultVisualization />} />
			<Route path="/events/:eventId" element={<Event />} />
			<Route path="/events/:eventId/tests/:testId" element={<TestResult />} />
			<Route path="/events/:eventId/player_match_performance" element={<PlayerMatchPerformanceRating />} />
			<Route path="/events/:eventId/peerSurvey" element={<PeerSurvey />} />
			<Route path="/events/:eventId/exercise_planner" element={<ExercisePlanner />} />
			<Route path="/exercises/:exerciseId/exercise_planner" element={<ExercisePlanner />} />
			<Route path="/exercise-sets/:exerciseSetId/exercise_planner" element={<ExercisePlanner />} />
			<Route path="/invite_patron" element={<InvitePatron useRootGroupCtx={USE_ROOT_CTX} />} />
			<Route path="/register" element={<Register />} />
			<Route path="/registerchild" element={<RegistrationMember childcreation />} />
			<Route path="/invite_member" element={<InviteMember useRootGroupCtx={USE_ROOT_CTX} />} />
			<Route path="/groups" element={<GroupList useRootGroupCtx={USE_ROOT_CTX} />} />
			<Route path="/groups/create" element={<GroupCreate />} />
			<Route path="/groups/:id" element={<GroupDetail />} />
			<Route path="/groups/root/create" element={<RootGroupCreate />} />
			<Route path="/groups/root/:id" element={<RootGroupDetail />} />
			<Route path="/profile" element={<Profile own />} />
			<Route path="/profile/:id" element={<Profile />} />
			<Route path="/child_invite_confirm" element={<ConfirmMemberInviteExisting acceptChild />} />
			<Route path="/activatechild" element={<ChildAccountConfirmation />} />
			<Route path="/edit_memberships" element={<EditMemberships useRootGroupCtx={USE_ROOT_CTX} />} />
			<Route path="/tasks" element={<TaskOverview />} />
			<Route path="/video/general" element={<VideoGeneral />} />
			<Route path="/video/rootGroup/:id" element={<VideoRootGroup />} />
			<Route path="/plans/period_planner" element={<PeriodPlannerPage />} />
			<Route path="/plans/period_planner/:id" element={<PeriodPlannerEditOrCreate />} />
			<Route path="/absence" element={<Absence />} />
			<Route path="/eventTemplate" element={<EventTemplateList />} />
			<Route path="/eventTemplate/:id" element={<EventTemplate />} />
			{SHOW_DEV_ONLY_COMPONENTS && <Route path="/migration" element={<MigrationView />} />}
			<Route path={"/exerciseDashboard"} element={<ExerciseDashboard />} />
			<Route path="/privacy" element={<PrivacyPolicyMarkdown />} />
		</Route>
		{/* No padding layout */}
		<Route element={<AuthenticatedLayout fullWidth noPadding />}>
			<Route path="/player_card/:id" element={<PlayerCard />} />
			<Route path="/player_card" element={<PlayerCardList />} />
		</Route>
	</Route>
)

export default MainRoute
